import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import cacheService from '../services/cacheService';
import cacheInitializer from '../services/cacheInitializer';

const DebugPanel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 10px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  z-index: 10000;
  max-width: 350px;
  max-height: 80vh;
  overflow-y: auto;
  ${props => !props.visible && 'display: none;'}
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 10px;
  right: 10px;
  background: #333;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  z-index: 10001;
  font-size: 11px;
`;

const StatusItem = styled.div`
  margin: 5px 0;
  padding: 5px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
`;

const ActionButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 3px 8px;
  margin: 2px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;
  
  &:hover {
    background: #45a049;
  }
`;

const ClearButton = styled(ActionButton)`
  background: #f44336;
  
  &:hover {
    background: #da190b;
  }
`;

const InitButton = styled(ActionButton)`
  background: #2196F3;
  
  &:hover {
    background: #0b7dda;
  }
`;

const InitStatus = styled.div`
  background: rgba(33, 150, 243, 0.2);
  border: 1px solid #2196F3;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 10px;
`;

const CacheDebugPanel = () => {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState({});
  const [initStatus, setInitStatus] = useState({});

  const updateStatus = () => {
    setStatus(cacheService.getStatus());
    setInitStatus(cacheInitializer.getStatus());
  };

  useEffect(() => {
    if (visible) {
      updateStatus();
      const interval = setInterval(updateStatus, 2000); // Update every 2 seconds
      return () => clearInterval(interval);
    }
  }, [visible]);

  const handleClearCache = (cacheType) => {
    cacheService.invalidate(cacheType);
    updateStatus();
  };

  const handleClearAll = () => {
    cacheService.clearAll();
    updateStatus();
  };

  const handleForceRefresh = async (cacheType) => {
    try {
      await cacheService.forceRefresh(cacheType);
      updateStatus();
    } catch (error) {
      console.error('Error force refreshing:', error);
    }
  };

  const handleInitEssential = async () => {
    try {
      await cacheInitializer.initializeEssentialData();
      updateStatus();
    } catch (error) {
      console.error('Error initializing essential data:', error);
    }
  };

  const handleInitUser = async () => {
    try {
      await cacheInitializer.initializeUserData();
      updateStatus();
    } catch (error) {
      console.error('Error initializing user data:', error);
    }
  };

  const handleInitAll = async () => {
    try {
      await cacheInitializer.initializeAllData();
      updateStatus();
    } catch (error) {
      console.error('Error initializing all data:', error);
    }
  };

  // Multiple safeguards to ensure this never shows in production
  if (process.env.NODE_ENV === 'production' || 
      process.env.REACT_APP_ENV === 'production' ||
      window.location.hostname !== 'localhost') {
    return null; // Don't show in production
  }

  return (
    <>
      <ToggleButton onClick={() => setVisible(!visible)}>
        Cache Debug {visible ? '🗙' : '🔧'}
      </ToggleButton>
      
      <DebugPanel visible={visible}>
        <h4 style={{ margin: '0 0 10px 0', borderBottom: '1px solid #666', paddingBottom: '5px' }}>
          Cache Debug Panel
        </h4>
        
        <InitStatus>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
            Cache Initializer Status
          </div>
          <div>Initialized: {initStatus.isInitialized ? '✅' : '❌'}</div>
          <div>Initializing: {initStatus.isInitializing ? '🔄' : '➖'}</div>
          <div style={{ marginTop: '5px' }}>
            <InitButton onClick={handleInitEssential}>Init Essential</InitButton>
            <InitButton onClick={handleInitUser}>Init User</InitButton>
            <InitButton onClick={handleInitAll}>Init All</InitButton>
          </div>
        </InitStatus>
        
        <div style={{ marginBottom: '10px' }}>
          <ClearButton onClick={handleClearAll}>Clear All Caches</ClearButton>
        </div>

        {Object.entries(status).map(([cacheType, info]) => (
          <StatusItem key={cacheType}>
            <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>
              {cacheType}
            </div>
            
            {info.exists ? (
              <div>
                <div>Age: {info.ageHours}h</div>
                <div>Status: {info.expired ? '🔴 Expired' : info.needsRefresh ? '🟡 Stale' : '🟢 Fresh'}</div>
                <div style={{ marginTop: '5px' }}>
                  <ActionButton onClick={() => handleForceRefresh(cacheType)}>
                    Refresh
                  </ActionButton>
                  <ClearButton onClick={() => handleClearCache(cacheType)}>
                    Clear
                  </ClearButton>
                </div>
              </div>
            ) : (
              <div>
                <div>❌ Not cached</div>
                <ActionButton onClick={() => handleForceRefresh(cacheType)}>
                  Load
                </ActionButton>
              </div>
            )}
          </StatusItem>
        ))}
      </DebugPanel>
    </>
  );
};

export default CacheDebugPanel; 